import React, { useState, useEffect, useRef } from 'react';
import { Upload, X, Loader2, Star } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { adminApi } from '../utils';

// Drag & Drop Image Upload Component
const ImageDropZone = ({ onImageAdd, images = [], onImageRemove, maxImages = 10, label = "Drop images here or click to browse" }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [imageUrls, setImageUrls] = useState(images);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setImageUrls(images);
  }, [images]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    // Check for dropped files first
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      await handleFiles(e.dataTransfer.files);
      return;
    }
    
    // Fallback to URL drop
    const text = e.dataTransfer.getData('text');
    if (text && (text.startsWith('http://') || text.startsWith('https://'))) {
      addImageUrl(text);
    }
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList);
    const token = localStorage.getItem('admin_token');
    
    if (!token) {
      toast.error('Please log in again');
      return;
    }
    
    if (imageUrls.length + files.length > maxImages) {
      toast.error(`Maximum ${maxImages} images allowed`);
      return;
    }

    setUploading(true);
    const uploadedUrls = [];

    for (const file of files) {
      // Validate file type
      if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
        toast.error(`${file.name} is not a valid image type`);
        continue;
      }

      // Validate file size (50MB)
      if (file.size > 50 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 50MB)`);
        continue;
      }

      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await adminApi.upload('/admin/upload-product-image', formData);

        if (response.data.success) {
          const fullUrl = response.data.url;
          uploadedUrls.push(fullUrl);
          toast.success(`Uploaded ${file.name}`);
        }
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
        console.error('Upload error:', error);
      }
    }

    // Use functional update to avoid stale state
    if (uploadedUrls.length > 0) {
      setImageUrls(prev => {
        const newUrls = [...prev, ...uploadedUrls];
        onImageAdd(newUrls);
        return newUrls;
      });
    }
    setUploading(false);
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const addImageUrl = (url) => {
    setImageUrls(prev => {
      if (prev.length >= maxImages) {
        toast.error(`Maximum ${maxImages} images allowed`);
        return prev;
      }
      const newUrls = [...prev, url];
      onImageAdd(newUrls);
      return newUrls;
    });
  };

  const removeImage = (index) => {
    setImageUrls(prev => {
      const newUrls = prev.filter((_, i) => i !== index);
      onImageRemove(newUrls);
      return newUrls;
    });
  };

  const setAsMain = (index) => {
    if (index === 0) return; // Already main
    setImageUrls(prev => {
      const newUrls = [...prev];
      const [movedImage] = newUrls.splice(index, 1);
      newUrls.unshift(movedImage);
      onImageAdd(newUrls);
      toast.success('Image set as main');
      return newUrls;
    });
  };

  const handleUrlInput = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const url = e.target.value.trim();
      if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
        addImageUrl(url);
        e.target.value = '';
      }
    }
  };

  return (
    <div className="space-y-3">
      <Label>{label}</Label>
      
      {imageUrls.length > 0 && (
        <div className="grid grid-cols-4 gap-2 mb-3">
          {imageUrls.map((url, index) => (
            <div key={index} className="relative group aspect-square rounded-lg overflow-hidden bg-secondary/30 border">
              <img src={url} alt={`Image ${index + 1}`} className="w-full h-full object-cover" onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f0f0f0" width="100" height="100"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%23999" font-size="12">No Image</text></svg>'; }} />
              
              {/* Delete button */}
              <button
                type="button"
                onClick={() => removeImage(index)}
                className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                title="Remove image"
              >
                <X className="w-3 h-3" />
              </button>
              
              {/* Set as Main button - only show for non-main images */}
              {index !== 0 && (
                <button
                  type="button"
                  onClick={() => setAsMain(index)}
                  className="absolute top-1 left-1 w-6 h-6 bg-amber-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-amber-600"
                  title="Set as main image"
                >
                  <Star className="w-3 h-3" />
                </button>
              )}
              
              {/* Main badge */}
              {index === 0 && (
                <span className="absolute bottom-1 left-1 text-xs bg-primary text-white px-1.5 py-0.5 rounded flex items-center gap-1">
                  <Star className="w-3 h-3 fill-current" /> Main
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept="image/jpeg,image/png,image/webp,image/gif"
        multiple
        className="hidden"
      />

      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-all cursor-pointer ${
          isDragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
        } ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
      >
        {uploading ? (
          <>
            <Loader2 className="w-8 h-8 mx-auto mb-2 text-primary animate-spin" />
            <p className="text-sm text-primary">Uploading...</p>
          </>
        ) : (
          <>
            <Upload className={`w-8 h-8 mx-auto mb-2 ${isDragging ? 'text-primary' : 'text-muted-foreground'}`} />
            <p className="text-sm text-muted-foreground mb-1">
              {isDragging ? 'Drop files here!' : 'Click to upload or drag & drop images'}
            </p>
            <p className="text-xs text-muted-foreground">JPEG, PNG, WebP, GIF • Max 50MB each</p>
          </>
        )}
      </div>

      <div className="flex items-center gap-2">
        <div className="flex-1 border-t border-border"></div>
        <span className="text-xs text-muted-foreground">or paste URL</span>
        <div className="flex-1 border-t border-border"></div>
      </div>

      <Input
        placeholder="Paste image URL and press Enter..."
        onKeyDown={handleUrlInput}
        className="text-sm"
      />
      
      <p className="text-xs text-muted-foreground">
        {imageUrls.length}/{maxImages} images • First image is main • Hover to ⭐ set as main or ✕ remove
      </p>
    </div>
  );
};

export default ImageDropZone;
